import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Package, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FadeUp } from "@/components/Reveal";

const statusStyles = {
  awaiting_payment: "text-destructive border-destructive",
  paid: "text-accent border-accent",
  shipped: "text-foreground border-foreground",
  delivered: "text-foreground border-border",
  cancelled: "text-muted-foreground border-border",
};

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="account-page">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <FadeUp>
            <p className="label text-accent mb-6">/ Account</p>
            <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.9] tracking-tight">
              Hello, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="mt-4 text-sm text-muted-foreground font-mono">{user?.email}</p>
          </FadeUp>
          <button onClick={handleLogout} className="label inline-flex items-center gap-2 border border-border px-5 py-3 hover:bg-secondary transition-colors self-start" data-testid="account-logout">
            <LogOut size={16} strokeWidth={1.5} /> Log out
          </button>
        </div>

        <div className="mt-16">
          <h2 className="font-display text-3xl font-light mb-8">Order history</h2>

          {loading ? (
            <p className="label text-muted-foreground animate-pulse">Loading…</p>
          ) : orders.length === 0 ? (
            <div className="border border-border p-12 flex flex-col items-start gap-5" data-testid="no-orders">
              <Package size={36} strokeWidth={1} className="text-muted-foreground" />
              <p className="text-sm text-muted-foreground">You have not placed any orders yet.</p>
              <Link to="/shop" className="label inline-flex items-center gap-2 border-b border-foreground pb-1 hover:text-accent hover:border-accent transition-colors" data-testid="account-shop-cta">
                Start shopping <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <div className="border-t border-border" data-testid="orders-list">
              {orders.map((o) => (
                <div key={o.id} className="py-6 border-b border-border" data-testid={`order-${o.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-6">
                      <span className="font-mono text-sm">#{o.id.slice(-6).toUpperCase()}</span>
                      <span className={`label px-3 py-1 border ${statusStyles[o.status] || "text-muted-foreground border-border"}`}>{(o.status || "").replace(/_/g, " ")}</span>
                    </div>
                    <div className="flex items-center gap-6">
                      <span className="text-xs text-muted-foreground font-mono">{new Date(o.created_at).toLocaleDateString()}</span>
                      <span className="font-mono text-sm">${o.total.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-muted-foreground">
                    {o.items.map((i) => `${i.name} × ${i.quantity}`).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
